import React, { useMemo, useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import * as api from '../api'
import { useHR } from '../../hr/HRContext'
import {
  can, clearPy, Confirm, dmy, EditModal, ErrorBox, exportRows, Filters, invalidate, Loading, matches, n2, printRows, PyHeader, Toolbar, usePyData, usePyMeta, usePyPeriod, Workbook,
} from '../py'
import { Select } from '../../hr/components/Controls'

// Stephanet's Projects (custom/pay15/Projects.jsx), ported unchanged except: a project is an Organisation site here, so a new one also
// needs its branch and code; Location is the site's address and State comes from the branch.

const HAZ = ['NONE', 'FIXED', '% OF BASIC']
export const projectFields = meta => [
  { k: 'name', label: 'Project / site name', req: true, span: 2 },
  { k: 'branch_id', label: 'Branch', type: 'select', req: true, options: (meta?.branches || []).map(b => ({ value: String(b.id), label: b.name })), show: f => !f.id },
  { k: 'code', label: 'Project code', req: true, hint: 'Short code, unique in the branch', show: f => !f.id },
  { k: 'site_status', label: 'Status', type: 'select', options: ['Active', 'Closed', 'On Hold'], blank: false },
  { k: 'sheet_name', label: 'Attendance sheet name', hint: 'Short tab name, e.g. RIVERS MALL MEP' },
  { k: 'start_date', label: 'Start date', type: 'date' }, { k: 'end_date', label: 'End date', type: 'date' },
  { k: 'site_allowance', label: 'Site allowance (₦ / month)', type: 'money' },
  { k: 'hazard_type', label: 'Hazard allowance type', type: 'select', options: HAZ, blank: false },
  { k: 'hazard_value', label: 'Hazard value', type: 'number', hint: '₦ amount when FIXED, or a fraction of Basic for % OF BASIC (0.05 = 5%)', show: f => f.hazard_type !== 'NONE' },
  { k: 'outstation_allowance', label: 'Outstation allowance (₦ / month)', type: 'money' },
  { k: 'location', label: 'Location' },
  { k: 'alias1', label: 'Other name in attendance reports (1)', hint: 'Only needed if your attendance report spells the site differently' },
  { k: 'alias2', label: 'Other name in attendance reports (2)' },
  { k: 'tp_each_day', label: 'TP (site transport) for each day present', type: 'check' },
  { k: 'site_notes', label: 'Notes', type: 'textarea' },
]

export default function Projects() {
  const { notify } = useHR()
  const [meta] = usePyMeta()
  const [period] = usePyPeriod(meta)
  const [d, err, reload] = usePyData(period)
  const [f, setF] = useState({ q: '', status: '' })
  const [edit, setEdit] = useState(null)
  const [del, setDel] = useState(null)
  const rows = useMemo(() => {
    if (!d) return []
    const paid = d.rows.filter(r => r.paid)
    return d.projects.map((p, i) => ({
      ...p, no: i + 1, staff: paid.filter(r => r.site === p.name).length,
      hazard: p.hazard_type === 'FIXED' ? n2(p.hazard_value) : p.hazard_type === '% OF BASIC' ? `${+(p.hazard_value * 100).toFixed(2)}% of Basic` : '—',
    })).filter(r => (!f.status || (r.status || '').toUpperCase() === f.status) && matches(r, f.q, ['name', 'sheet', 'alias1', 'alias2', 'location', 'state']))
  }, [d, f])
  if (err) return <ErrorBox error={err} />
  if (!d) return <Loading what="projects" />
  const cols = [
    { key: 'name', label: 'Project / site name', w: 220 }, { key: 'status', label: 'Status', w: 80 }, { key: 'start', label: 'Start', get: r => dmy(r.start), w: 100 },
    { key: 'end', label: 'End', get: r => dmy(r.end), w: 100 }, { key: 'sheet', label: 'Attendance sheet', w: 160 },
    { key: 'site_allowance', label: 'Site allowance (₦/month)', kind: 'money' }, { key: 'hazard_type', label: 'Hazard type', w: 100 },
    { key: 'hazard', label: 'Hazard value', w: 120 }, { key: 'outstation', label: 'Outstation (₦/month)', kind: 'money' },
    { key: 'location', label: 'Location', w: 120 }, { key: 'state', label: 'State', w: 100 }, { key: 'alias1', label: 'Other name (1)', w: 140 }, { key: 'alias2', label: 'Other name (2)', w: 140 },
    { key: 'tp', label: 'TP each day', get: r => (r.tp ? 'Y' : 'N'), w: 80 }, { key: 'staff', label: 'Staff based here', kind: 'num', sum: true },
  ]
  const ok = can(meta, 'py-projects')
  const done = async (r) => { notify(r.message); setEdit(null); setDel(null); clearPy(); invalidate(); reload() }
  const toForm = p => ({ id: p.id, name: p.name, site_status: (p.status || 'ACTIVE').charAt(0) + (p.status || 'ACTIVE').slice(1).toLowerCase(), sheet_name: p.sheet,
    start_date: p.start, end_date: p.end, site_allowance: p.site_allowance, hazard_type: p.hazard_type, hazard_value: p.hazard_value, outstation_allowance: p.outstation,
    location: p.location, state: p.state, alias1: p.alias1, alias2: p.alias2, tp_each_day: p.tp, site_notes: p.notes })
  return (
    <div className="ps py">
      <PyHeader title="Projects" sub="Projects / sites with status, site, hazard and outstation allowances and the names used in attendance reports.">
        <Toolbar onExport={() => exportRows('projects.csv', cols, rows)} onPrint={() => printRows('Projects', `${rows.length} project(s)`, cols, rows)}>
          {can(meta, 'py-projects', 'create') && <button className="mds-run" onClick={() => setEdit({ site_status: 'Active', hazard_type: 'NONE' })}><Plus size={14} /> Add Project</button>}
        </Toolbar>
      </PyHeader>
      <Filters meta={meta} f={f} setF={setF} show={['q']}>
        <label className="ps-field"><span>Status</span><Select title="Status" value={f.status} onChange={e => setF({ ...f, status: e.target.value })}><option value="">All</option><option>ACTIVE</option><option>CLOSED</option><option>ON HOLD</option></Select></label>
      </Filters>
      <Workbook cols={cols} rows={rows} frozen={1} onRow={ok ? r => setEdit(toForm(r)) : undefined} empty="No projects." />
      {edit && <EditModal title={edit.id ? `Edit ${edit.name}` : 'Add Project'} value={edit} fields={projectFields(meta)} onClose={() => setEdit(null)}
        onSave={async v => done(await api.saveSite(v.id, { ...v, hazard_value: Number(v.hazard_value || 0) }))}>
        {v => v.id && can(meta, 'py-projects', 'delete') ? <button className="mdr-btn danger py-del" onClick={() => setDel(v)}><Trash2 size={13} /> Delete project</button> : null}
      </EditModal>}
      {del && <Confirm title="Delete project" text={`Delete ${del.name}? Projects with staff or attendance cannot be deleted — set them to Closed instead.`} danger yes="Delete"
        onClose={() => setDel(null)} onYes={async () => { try { done(await api.deleteSite(del.id)) } catch (e) { notify(e.message, 'bad'); setDel(null) } }} />}
      <p className="muted small py-foot"><Pencil size={12} /> Click a project to edit it. A new project gets its own site sheet automatically, and is a site everywhere in the app.</p>
    </div>
  )
}
